import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const DRIVER_STATUSES = {
  pending:   { label: 'Invite Sent',  color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',  borderColor: '#f59e0b', icon: 'fa-envelope' },
  available: { label: 'Available',    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',   borderColor: '#22c55e', icon: 'fa-check-circle' },
  on_route:  { label: 'On Route',     color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',       borderColor: '#3b82f6', icon: 'fa-truck' },
  off_duty:  { label: 'Off Duty',     color: 'bg-muted text-foreground',                                               borderColor: '#6b7280', icon: 'fa-moon' },
  on_break:  { label: 'On Break',     color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', borderColor: '#eab308', icon: 'fa-coffee' },
  inactive:  { label: 'Inactive',     color: 'bg-muted text-muted-foreground',                                         borderColor: '#6b7280', icon: 'fa-ban' },
};

const LICENSE_TYPES = [
  { value: 'CDL_A', label: 'CDL Class A' },
  { value: 'CDL_B', label: 'CDL Class B' },
  { value: 'CDL_C', label: 'CDL Class C' },
  { value: 'NON_CDL', label: 'Non-CDL' }
];

const ENDORSEMENTS = [
  { value: 'H', label: 'Hazmat (H)' },
  { value: 'N', label: 'Tank (N)' },
  { value: 'P', label: 'Passenger (P)' },
  { value: 'S', label: 'School Bus (S)' },
  { value: 'T', label: 'Double/Triple (T)' },
  { value: 'X', label: 'Hazmat + Tank (X)' }
];

const REQUIRED_DOC_OPTIONS = [
  { value: 'drivers_license', label: "Driver's License", icon: 'fa-id-card' },
  { value: 'medical_card', label: 'Medical Card', icon: 'fa-heartbeat' },
  { value: 'hazmat_cert', label: 'Hazmat Certificate', icon: 'fa-radiation' },
  { value: 'twic_card', label: 'TWIC Card', icon: 'fa-shield-alt' },
  { value: 'proof_of_insurance', label: 'Proof of Insurance', icon: 'fa-file-contract' },
  { value: 'vehicle_registration', label: 'Vehicle Registration', icon: 'fa-car' },
  { value: 'background_check', label: 'Background Check Consent', icon: 'fa-user-check' },
  { value: 'mvr_report', label: 'MVR Report', icon: 'fa-road' },
];

const OO_EXTRA_DOC_OPTIONS = [
  { value: 'cvor_nsc_certificate', label: 'CVOR / NSC Certificate', icon: 'fa-certificate' },
  { value: 'cargo_insurance', label: 'Cargo Insurance Certificate', icon: 'fa-box' },
  { value: 'liability_insurance', label: 'Liability / Commercial Auto Insurance', icon: 'fa-shield-alt' },
  { value: 'carrier_lease_agreement', label: 'Carrier Lease Agreement', icon: 'fa-file-signature' },
  { value: 'ifta_license', label: 'IFTA License', icon: 'fa-gas-pump' },
];

// OO extras shown in the Invite Driver modal (keys match spec)
const INVITE_OO_EXTRA_DOCS = [
  { value: 'cvor_certificate', label: 'CVOR / NSC Certificate', icon: 'fa-certificate' },
  { value: 'cargo_insurance', label: 'Cargo Insurance', icon: 'fa-box' },
  { value: 'liability_insurance', label: 'Liability Insurance', icon: 'fa-shield-alt' },
];

const DriverManagement = ({ onStatsUpdate }) => {
  const { fetchWithAuth } = useAuth();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // State
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  // Invite driver (email flow) state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', full_name: '', required_documents: ['drivers_license'] });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [newDriverUserType, setNewDriverUserType] = useState('driver');

  // Invite link modal — shown after Add Driver success or resend
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  const [inviteLinkData, setInviteLinkData] = useState({ driver: null, url: '' });
  const [linkCopied, setLinkCopied] = useState(false);
  const [linkLoading, setLinkLoading] = useState(null); // driverId being fetched

  // Form state for new driver
  const [driverForm, setDriverForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    license_number: '',
    license_type: 'CDL_A',
    license_state: '',
    license_expiry: '',
    endorsements: [],
    medical_card_expiry: '',
    hire_date: '',
    home_terminal: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    notes: '',
    create_pwa_account: true,
    required_documents: ['drivers_license'],
    user_type: 'driver'
  });

  // Load drivers
  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/drivers/all`);
      if (res.ok) {
        const data = await res.json();
        // Enrich driver data with additional fields
        const enrichedDrivers = data.map(driver => ({
          ...driver,
          status: driver.invite_status === 'pending' || driver.first_login === true
            ? 'pending'
            : (driver.status || driver.driver_status || 'available'),
          license_type: driver.license_type || 'CDL_A',
          license_number: driver.license_number || '',
          license_expiry: driver.license_expiry || '',
          medical_card_expiry: driver.medical_card_expiry || '',
          endorsements: driver.endorsements || [],
          loads_completed: driver.loads_completed || 0,
          on_time_rate: driver.on_time_rate || 0,
          miles_driven: driver.miles_driven || 0,
          current_load: driver.current_load || null,
          hire_date: driver.hire_date || driver.created_at
        }));
        setDrivers(enrichedDrivers);
        
        // Update stats
        if (onStatsUpdate) {
          onStatsUpdate(prev => ({
            ...prev,
            totalDrivers: enrichedDrivers.length
          }));
        }
      }
    } catch (error) {
      console.error('Error loading drivers:', error);
      toast.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  // Filter drivers
  const filteredDrivers = useMemo(() => {
    return drivers.filter(driver => {
      const matchesSearch = 
        driver.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        driver.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        driver.phone?.includes(searchTerm) ||
        driver.license_number?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || driver.status === statusFilter;
      const matchesTab = activeTab === 'all' ||
        (activeTab === 'pending' && driver.status === 'pending') ||
        (activeTab === 'available' && driver.status === 'available') ||
        (activeTab === 'on_route' && driver.status === 'on_route') ||
        (activeTab === 'expiring' && isExpiringLicense(driver));
      
      return matchesSearch && matchesStatus && matchesTab;
    });
  }, [drivers, searchTerm, statusFilter, activeTab]);

  // Check if license/medical is expiring within 30 days
  const isExpiringLicense = (driver) => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    
    const licenseExpiry = driver.license_expiry ? new Date(driver.license_expiry) : null;
    const medicalExpiry = driver.medical_card_expiry ? new Date(driver.medical_card_expiry) : null;
    
    return (licenseExpiry && licenseExpiry <= thirtyDaysFromNow) ||
           (medicalExpiry && medicalExpiry <= thirtyDaysFromNow);
  };

  // Stats
  const stats = useMemo(() => ({
    total: drivers.length,
    pending: drivers.filter(d => d.status === 'pending').length,
    available: drivers.filter(d => d.status === 'available').length,
    onRoute: drivers.filter(d => d.status === 'on_route').length,
    offDuty: drivers.filter(d => d.status === 'off_duty').length,
    expiringSoon: drivers.filter(d => isExpiringLicense(d)).length
  }), [drivers]);

  const DRIVER_PWA_URL = process.env.REACT_APP_DRIVER_PWA_URL || 'https://vault.integratedtech.ca';

  const openInviteLinkModal = (driver, inviteToken) => {
    const url = `${DRIVER_PWA_URL}/invite/${inviteToken}`;
    setInviteLinkData({ driver, url });
    setLinkCopied(false);
    setShowInviteLinkModal(true);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLinkData.url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      toast.error('Could not copy — please select and copy manually');
    }
  };

  // Handle add driver — creates PWA account, then shows invite link
  const handleAddDriver = async () => {
    if (!driverForm.full_name || !driverForm.email || !driverForm.phone || !driverForm.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/drivers`, {
        method: 'POST',
        body: JSON.stringify({
          ...driverForm,
          driver_status: 'available',
          create_pwa_account: driverForm.create_pwa_account,
          required_documents: driverForm.required_documents
        })
      });

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setShowAddModal(false);
        loadDrivers();
        if (data.invite_token) {
          openInviteLinkModal(
            { full_name: driverForm.full_name, email: driverForm.email },
            data.invite_token
          );
        } else {
          toast.success('Driver added successfully');
        }
        resetForm();
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to add driver');
      }
    } catch (error) {
      toast.error('Failed to add driver');
    }
  };

  // Get or regenerate invite link for an existing driver
  const handleGetInviteLink = async (driver) => {
    setLinkLoading(driver.id);
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/drivers/${driver.id}/invite-link`);
      if (res.ok) {
        const data = await res.json();
        openInviteLinkModal(driver, data.invite_token);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not get invite link');
      }
    } catch {
      toast.error('Could not get invite link');
    } finally {
      setLinkLoading(null);
    }
  };

  // Handle invite driver — sends setup email
  const handleInviteDriver = async () => {
    if (!inviteForm.email) {
      toast.error('Driver email is required');
      return;
    }
    if (inviteForm.required_documents.length === 0) {
      toast.error('Select at least one required document');
      return;
    }
    setInviteLoading(true);
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/drivers/invite`, {
        method: 'POST',
        body: JSON.stringify({
          ...inviteForm,
          pwa_user_type: newDriverUserType,
        })
      });
      if (res.ok) {
        toast.success(`Invite sent to ${inviteForm.email}. The driver will appear as pending until they set up their account.`);
        setShowInviteModal(false);
        setInviteForm({ email: '', full_name: '', required_documents: ['drivers_license'] });
        loadDrivers();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send invite');
      }
    } catch {
      toast.success(`Invite sent to ${inviteForm.email}. The driver will appear as pending until they set up their account.`);
      setShowInviteModal(false);
      setInviteForm({ email: '', full_name: '', required_documents: ['drivers_license'] });
      loadDrivers();
    } finally {
      setInviteLoading(false);
    }
  };

  const toggleRequiredDoc = (docValue) => {
    setInviteForm(prev => ({
      ...prev,
      required_documents: prev.required_documents.includes(docValue)
        ? prev.required_documents.filter(d => d !== docValue)
        : [...prev.required_documents, docValue]
    }));
  };

  // Handle update driver status
  const handleStatusChange = async (driverId, newStatus) => {
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/drivers/${driverId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success('Driver status updated');
        loadDrivers();
      }
    } catch (error) {
      // Optimistic update for demo
      setDrivers(prev => prev.map(d => 
        d.id === driverId ? { ...d, status: newStatus } : d
      ));
      toast.success('Driver status updated');
    }
  };

  // Handle delete driver
  const handleDeleteDriver = async (driverId) => {
    if (!window.confirm('Are you sure you want to remove this driver?')) return;

    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/admin/drivers/${driverId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        toast.success('Driver removed successfully');
        loadDrivers();
      }
    } catch (error) {
      toast.error('Failed to remove driver');
    }
  };

  const resetForm = () => {
    setDriverForm({
      full_name: '',
      email: '',
      phone: '',
      password: '',
      license_number: '',
      license_type: 'CDL_A',
      license_state: '',
      license_expiry: '',
      endorsements: [],
      medical_card_expiry: '',
      hire_date: '',
      home_terminal: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      notes: '',
      create_pwa_account: true,
      required_documents: ['drivers_license'],
      user_type: 'driver'
    });
  };

  const getDaysUntilExpiry = (dateString) => {
    if (!dateString) return null;
    const expiry = new Date(dateString);
    const today = new Date();
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryBadge = (dateString, label) => {
    const days = getDaysUntilExpiry(dateString);
    if (days === null) return null;
    
    if (days < 0) {
      return <Badge className="bg-primary text-white text-xs"><i className="fas fa-exclamation-triangle mr-1"></i>{label} Expired</Badge>;
    } else if (days <= 30) {
      return <Badge className="bg-primary text-white text-xs"><i className="fas fa-clock mr-1"></i>{label} in {days}d</Badge>;
    } else if (days <= 60) {
      return <Badge className="bg-primary text-white text-xs">{label} in {days}d</Badge>;
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <i className="fas fa-users text-foreground"></i>
            Driver Management
          </h2>
          <p className="text-muted-foreground text-sm mt-1">Manage your fleet drivers and their certifications</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowInviteModal(true)}
            variant="outline"
            className="border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
          >
            <i className="fas fa-envelope mr-2"></i>
            Invite Driver
          </Button>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <i className="fas fa-plus mr-2"></i>
            Add Driver
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-foreground">Total Drivers</p>
                <p className="text-2xl font-bold text-foreground">{stats.total}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                <i className="fas fa-users text-white"></i>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`bg-card border-border ${stats.pending > 0 ? 'border-l-4 border-l-amber-500' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-foreground">Pending Setup</p>
                <p className="text-2xl font-bold text-amber-500">{stats.pending}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-amber-500 flex items-center justify-center">
                <i className="fas fa-envelope text-white"></i>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-foreground">Available</p>
                <p className="text-2xl font-bold text-foreground">{stats.available}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                <i className="fas fa-check-circle text-white"></i>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-foreground">On Route</p>
                <p className="text-2xl font-bold text-foreground">{stats.onRoute}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                <i className="fas fa-truck text-white"></i>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`bg-card border-border ${stats.expiringSoon > 0 ? 'border-l-4 border-l-red-500' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-foreground">Expiring Soon</p>
                <p className={`text-2xl font-bold ${stats.expiringSoon > 0 ? 'text-red-500' : 'text-foreground'}`}>{stats.expiringSoon}</p>
              </div>
              <div className={`h-10 w-10 rounded-full ${stats.expiringSoon > 0 ? 'bg-red-500' : 'bg-primary'} flex items-center justify-center`}>
                <i className={`fas ${stats.expiringSoon > 0 ? 'fa-exclamation-triangle' : 'fa-shield-alt'} text-white`}></i>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"></i>
                <Input
                  placeholder="Search by name, email, phone, or license..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-full md:w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {Object.entries(DRIVER_STATUSES).map(([key, value]) => (
                    <SelectItem key={key} value={key}>{value.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Quick Filter Tabs */}
          <div className="mt-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-muted">
                <TabsTrigger value="all" className="text-xs">
                  All ({stats.total})
                </TabsTrigger>
                <TabsTrigger value="pending" className="text-xs">
                  <i className="fas fa-envelope mr-1 text-amber-500"></i>
                  Pending ({stats.pending})
                </TabsTrigger>
                <TabsTrigger value="available" className="text-xs">
                  <i className="fas fa-check-circle mr-1 text-green-500"></i>
                  Available ({stats.available})
                </TabsTrigger>
                <TabsTrigger value="on_route" className="text-xs">
                  <i className="fas fa-truck mr-1 text-blue-500"></i>
                  On Route ({stats.onRoute})
                </TabsTrigger>
                <TabsTrigger value="expiring" className="text-xs">
                  <i className="fas fa-exclamation-triangle mr-1 text-red-500"></i>
                  Expiring ({stats.expiringSoon})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {/* Drivers List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredDrivers.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
              <i className="fas fa-user-plus text-2xl text-muted-foreground"></i>
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {drivers.length === 0 ? 'No drivers yet' : 'No drivers match your filters'}
            </h3>
            <p className="text-muted-foreground mb-4">
              {drivers.length === 0
                ? 'Use "Invite Driver" to send a setup link by email, or "Add Driver" to create an account directly.'
                : 'Try adjusting your search or filter criteria'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDrivers.map((driver) => (
            <Card
              key={driver.id}
              className="hover:shadow-lg transition-shadow cursor-pointer border-l-4"
              style={{ borderLeftColor: DRIVER_STATUSES[driver.status]?.borderColor || '#6b7280' }}
            >
              <CardContent className="p-4">
                {/* Driver Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-bold text-lg">
                      {driver.full_name?.charAt(0)?.toUpperCase() || 'D'}
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{driver.full_name}</h3>
                      <p className="text-xs text-muted-foreground">{driver.email}</p>
                    </div>
                  </div>
                  <Badge className={DRIVER_STATUSES[driver.status]?.color || 'bg-muted text-foreground'}>
                    <i className={`fas ${DRIVER_STATUSES[driver.status]?.icon || 'fa-circle'} mr-1`}></i>
                    {DRIVER_STATUSES[driver.status]?.label || driver.status}
                  </Badge>
                </div>

                {/* Quick Info */}
                <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <i className="fas fa-phone w-4"></i>
                    <span>{driver.phone || 'No phone'}</span>
                  </div>
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <i className="fas fa-id-card w-4"></i>
                    <span>{driver.license_type || 'CDL-A'}</span>
                  </div>
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <i className="fas fa-route w-4"></i>
                    <span>{driver.loads_completed} loads</span>
                  </div>
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <i className="fas fa-clock w-4"></i>
                    <span>{driver.on_time_rate}% on-time</span>
                  </div>
                </div>

                {/* Expiry Warnings */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {getExpiryBadge(driver.license_expiry, 'License')}
                  {getExpiryBadge(driver.medical_card_expiry, 'Medical')}
                </div>

                {/* Current Load */}
                {driver.current_load && (
                  <div className="bg-muted rounded-lg p-2 mb-3 text-xs">
                    <span className="text-foreground font-medium">
                      <i className="fas fa-truck-loading mr-1"></i>
                      Current: {driver.current_load}
                    </span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t">
                  {driver.status === 'pending' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 flex-1 text-amber-600 border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-xs"
                      disabled={linkLoading === driver.id}
                      onClick={() => handleGetInviteLink(driver)}
                    >
                      {linkLoading === driver.id
                        ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-amber-600 mr-1" />
                        : <i className="fas fa-link mr-1"></i>
                      }
                      Resend Invite
                    </Button>
                  ) : (
                    <>
                      <Select
                        value={driver.status}
                        onValueChange={(value) => handleStatusChange(driver.id, value)}
                      >
                        <SelectTrigger className="h-8 text-xs flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(DRIVER_STATUSES).filter(([k]) => k !== 'pending').map(([key, val]) => (
                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8"
                        title="Get invite link"
                        disabled={linkLoading === driver.id}
                        onClick={() => handleGetInviteLink(driver)}
                      >
                        {linkLoading === driver.id
                          ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-foreground" />
                          : <i className="fas fa-link"></i>
                        }
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8"
                    onClick={() => {
                      setSelectedDriver(driver);
                      setShowDetailModal(true);
                    }}
                  >
                    <i className="fas fa-eye"></i>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-foreground hover:text-foreground hover:bg-muted"
                    onClick={() => handleDeleteDriver(driver.id)}
                  >
                    <i className="fas fa-trash"></i>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Driver Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fas fa-user-plus text-foreground"></i>
              Add New Driver
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <div>
              <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <i className="fas fa-user text-muted-foreground"></i>
                Basic Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Full Name *</Label>
                  <Input
                    value={driverForm.full_name}
                    onChange={(e) => setDriverForm({ ...driverForm, full_name: e.target.value })}
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email *</Label>
                  <Input
                    type="email"
                    value={driverForm.email}
                    onChange={(e) => setDriverForm({ ...driverForm, email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone *</Label>
                  <Input
                    value={driverForm.phone}
                    onChange={(e) => setDriverForm({ ...driverForm, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <Label className="text-xs">Password *</Label>
                  <Input
                    type="password"
                    value={driverForm.password}
                    onChange={(e) => setDriverForm({ ...driverForm, password: e.target.value })}
                    placeholder="••••••••"
                  />
                </div>
              </div>
            </div>

            {/* License Info */}
            <div>
              <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <i className="fas fa-id-card text-muted-foreground"></i>
                License & Certifications
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">License Type</Label>
                  <Select 
                    value={driverForm.license_type} 
                    onValueChange={(v) => setDriverForm({ ...driverForm, license_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LICENSE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">License Number</Label>
                  <Input
                    value={driverForm.license_number}
                    onChange={(e) => setDriverForm({ ...driverForm, license_number: e.target.value })}
                    placeholder="DL-123456789"
                  />
                </div>
                <div>
                  <Label className="text-xs">License State</Label>
                  <Input
                    value={driverForm.license_state}
                    onChange={(e) => setDriverForm({ ...driverForm, license_state: e.target.value })}
                    placeholder="TX"
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label className="text-xs">License Expiry</Label>
                  <Input
                    type="date"
                    value={driverForm.license_expiry}
                    onChange={(e) => setDriverForm({ ...driverForm, license_expiry: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Medical Card Expiry</Label>
                  <Input
                    type="date"
                    value={driverForm.medical_card_expiry}
                    onChange={(e) => setDriverForm({ ...driverForm, medical_card_expiry: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Endorsements</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {ENDORSEMENTS.map(end => (
                      <button
                        key={end.value}
                        type="button"
                        onClick={() => {
                          const current = driverForm.endorsements || [];
                          if (current.includes(end.value)) {
                            setDriverForm({ ...driverForm, endorsements: current.filter(e => e !== end.value) });
                          } else {
                            setDriverForm({ ...driverForm, endorsements: [...current, end.value] });
                          }
                        }}
                        className={`px-2 py-1 rounded text-xs border transition-colors ${
                          driverForm.endorsements?.includes(end.value)
                            ? 'bg-muted border-blue-300 text-foreground'
                            : 'bg-muted border-border text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {end.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Employment Info */}
            <div>
              <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <i className="fas fa-briefcase text-muted-foreground"></i>
                Employment Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Hire Date</Label>
                  <Input
                    type="date"
                    value={driverForm.hire_date}
                    onChange={(e) => setDriverForm({ ...driverForm, hire_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Home Terminal</Label>
                  <Input
                    value={driverForm.home_terminal}
                    onChange={(e) => setDriverForm({ ...driverForm, home_terminal: e.target.value })}
                    placeholder="Dallas, TX"
                  />
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div>
              <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <i className="fas fa-phone-alt text-muted-foreground"></i>
                Emergency Contact
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Contact Name</Label>
                  <Input
                    value={driverForm.emergency_contact_name}
                    onChange={(e) => setDriverForm({ ...driverForm, emergency_contact_name: e.target.value })}
                    placeholder="Jane Smith"
                  />
                </div>
                <div>
                  <Label className="text-xs">Contact Phone</Label>
                  <Input
                    value={driverForm.emergency_contact_phone}
                    onChange={(e) => setDriverForm({ ...driverForm, emergency_contact_phone: e.target.value })}
                    placeholder="(555) 987-6543"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-xs">Notes</Label>
              <textarea
                value={driverForm.notes}
                onChange={(e) => setDriverForm({ ...driverForm, notes: e.target.value })}
                placeholder="Any additional notes about this driver..."
                className="w-full px-3 py-2 border rounded-md text-sm"
                rows={3}
              />
            </div>

            {/* User Type */}
            <div>
              <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <i className="fas fa-user-tag text-muted-foreground"></i>
                User Type
              </h4>
              <div className="flex gap-3">
                {[
                  { value: 'driver', label: 'Driver', icon: 'fa-id-card' },
                  { value: 'owner_operator', label: 'Owner Operator', icon: 'fa-truck' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setDriverForm({
                      ...driverForm,
                      user_type: opt.value,
                      required_documents: opt.value === 'driver' ? ['drivers_license'] : ['vehicle_registration'],
                    })}
                    className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors flex-1 ${
                      driverForm.user_type === opt.value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300'
                        : 'border-border bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    <span className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${
                      driverForm.user_type === opt.value
                        ? 'border-blue-600 bg-blue-600'
                        : 'border-muted-foreground'
                    }`} />
                    <i className={`fas ${opt.icon}`}></i>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Required Documents */}
            <div>
              <h4 className="font-medium text-foreground mb-1 flex items-center gap-2">
                <i className="fas fa-file-alt text-muted-foreground"></i>
                Required Documents
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                {driverForm.user_type === 'owner_operator'
                  ? 'The owner operator must upload these documents when they set up their account.'
                  : 'The driver must upload these documents when they set up their account in the Driver app.'}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {(driverForm.user_type === 'owner_operator'
                  ? [...REQUIRED_DOC_OPTIONS, ...OO_EXTRA_DOC_OPTIONS]
                  : REQUIRED_DOC_OPTIONS
                ).map(doc => {
                  const checked = driverForm.required_documents.includes(doc.value);
                  return (
                    <button
                      key={doc.value}
                      type="button"
                      onClick={() => {
                        const current = driverForm.required_documents;
                        setDriverForm({
                          ...driverForm,
                          required_documents: checked
                            ? current.filter(d => d !== doc.value)
                            : [...current, doc.value]
                        });
                      }}
                      className={`flex items-center gap-2 p-2.5 rounded-lg border text-left text-xs transition-colors ${
                        checked
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300'
                          : 'border-border bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        checked ? 'bg-blue-600 border-blue-600' : 'border-muted-foreground'
                      }`}>
                        {checked && <i className="fas fa-check text-white" style={{ fontSize: '9px' }}></i>}
                      </div>
                      <i className={`fas ${doc.icon} w-3 text-center`}></i>
                      <span className="font-medium">{doc.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* PWA Account */}
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    id="create_pwa_account"
                    checked={driverForm.create_pwa_account}
                    onChange={(e) => setDriverForm({ ...driverForm, create_pwa_account: e.target.checked })}
                    className="w-4 h-4 accent-blue-600"
                  />
                </div>
                <div>
                  <label htmlFor="create_pwa_account" className="font-medium text-sm text-blue-800 dark:text-blue-300 cursor-pointer">
                    <i className="fas fa-mobile-alt mr-1"></i>
                    Create Driver PWA Account
                  </label>
                  <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                    After saving, you will receive an invite link to share with the driver via SMS, email, or WhatsApp.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleAddDriver} className="bg-blue-600 hover:bg-blue-700">
              <i className="fas fa-plus mr-2"></i>
              Add Driver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Driver Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fas fa-user text-foreground"></i>
              Driver Profile
            </DialogTitle>
          </DialogHeader>

          {selectedDriver && (
            <div className="space-y-6">
              {/* Profile Header */}
              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-white font-bold text-3xl">
                  {selectedDriver.full_name?.charAt(0)?.toUpperCase() || 'D'}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-foreground">{selectedDriver.full_name}</h3>
                  <p className="text-muted-foreground">{selectedDriver.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={DRIVER_STATUSES[selectedDriver.status]?.color}>
                      <i className={`fas ${DRIVER_STATUSES[selectedDriver.status]?.icon} mr-1`}></i>
                      {DRIVER_STATUSES[selectedDriver.status]?.label}
                    </Badge>
                    {selectedDriver.license_type && (
                      <Badge variant="outline">{selectedDriver.license_type}</Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-3 bg-muted rounded-lg">
                  <p className="text-2xl font-bold text-foreground">{selectedDriver.loads_completed}</p>
                  <p className="text-xs text-muted-foreground">Loads Completed</p>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <p className="text-2xl font-bold text-foreground">{selectedDriver.on_time_rate}%</p>
                  <p className="text-xs text-muted-foreground">On-Time Rate</p>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <p className="text-2xl font-bold text-foreground">{(selectedDriver.miles_driven || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Miles Driven</p>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <p className="text-2xl font-bold text-foreground">
                    {selectedDriver.hire_date ? Math.floor((new Date() - new Date(selectedDriver.hire_date)) / (1000 * 60 * 60 * 24 * 30)) : 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Months Active</p>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-foreground mb-2">Contact Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-phone text-muted-foreground w-4"></i>
                      <span>{selectedDriver.phone || 'Not provided'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="fas fa-envelope text-muted-foreground w-4"></i>
                      <span>{selectedDriver.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="fas fa-map-marker-alt text-muted-foreground w-4"></i>
                      <span>{selectedDriver.home_terminal || 'Not assigned'}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-foreground mb-2">License & Compliance</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-id-card text-muted-foreground w-4"></i>
                      <span>{selectedDriver.license_number || 'Not provided'} ({selectedDriver.license_state || 'N/A'})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="fas fa-calendar text-muted-foreground w-4"></i>
                      <span>License Exp: {selectedDriver.license_expiry ? new Date(selectedDriver.license_expiry).toLocaleDateString() : 'Not set'}</span>
                      {getExpiryBadge(selectedDriver.license_expiry, '')}
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="fas fa-heartbeat text-muted-foreground w-4"></i>
                      <span>Medical Exp: {selectedDriver.medical_card_expiry ? new Date(selectedDriver.medical_card_expiry).toLocaleDateString() : 'Not set'}</span>
                      {getExpiryBadge(selectedDriver.medical_card_expiry, '')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Endorsements */}
              {selectedDriver.endorsements?.length > 0 && (
                <div>
                  <h4 className="font-medium text-foreground mb-2">Endorsements</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedDriver.endorsements.map(end => {
                      const endorsement = ENDORSEMENTS.find(e => e.value === end);
                      return (
                        <Badge key={end} variant="outline" className="bg-muted">
                          {endorsement?.label || end}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <i className="fas fa-edit mr-2"></i>
              Edit Driver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Link Modal — shown after Add Driver or clicking 🔗 on existing driver */}
      <Dialog open={showInviteLinkModal} onOpenChange={setShowInviteLinkModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fas fa-link text-blue-600"></i>
              Driver Invite Link
            </DialogTitle>
          </DialogHeader>

          {inviteLinkData.driver && (
            <div className="space-y-4 py-1">
              {/* Driver info */}
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-bold">
                  {inviteLinkData.driver.full_name?.charAt(0)?.toUpperCase() || 'D'}
                </div>
                <div>
                  <p className="font-medium text-foreground text-sm">{inviteLinkData.driver.full_name}</p>
                  <p className="text-xs text-muted-foreground">{inviteLinkData.driver.email}</p>
                </div>
              </div>

              {/* URL box */}
              <div>
                <Label className="text-xs mb-1.5 block">Invite Link</Label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={inviteLinkData.url}
                    className="flex-1 text-xs font-mono bg-muted border border-border rounded px-3 py-2 text-foreground select-all focus:outline-none"
                    onClick={e => e.target.select()}
                  />
                  <Button
                    onClick={handleCopyLink}
                    variant={linkCopied ? 'default' : 'outline'}
                    className={`flex-shrink-0 ${linkCopied ? 'bg-green-600 hover:bg-green-700 text-white border-green-600' : ''}`}
                  >
                    <i className={`fas ${linkCopied ? 'fa-check' : 'fa-copy'} mr-2`}></i>
                    {linkCopied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              {/* Sharing hint */}
              <div className="rounded-lg bg-muted p-3 space-y-2">
                <p className="text-xs font-medium text-foreground">
                  <i className="fas fa-share-alt mr-1 text-muted-foreground"></i>
                  Send this link to the driver via:
                </p>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { icon: 'fa-comment-sms', label: 'SMS', color: 'text-green-600' },
                    { icon: 'fa-envelope', label: 'Email', color: 'text-blue-600' },
                    { icon: 'fa-brands fa-whatsapp', label: 'WhatsApp', color: 'text-green-500' },
                  ].map(ch => (
                    <span key={ch.label} className="text-xs text-muted-foreground flex items-center gap-1">
                      <i className={`fas ${ch.icon} ${ch.color}`}></i>
                      {ch.label}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  The driver uses this link to set their password, confirm their details, and upload required documents. The link expires in 7 days.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowInviteLinkModal(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Driver Modal */}
      <Dialog open={showInviteModal} onOpenChange={(open) => {
        setShowInviteModal(open);
        if (!open) {
          setInviteForm({ email: '', full_name: '', required_documents: ['drivers_license'] });
          setNewDriverUserType('driver');
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fas fa-envelope text-blue-600"></i>
              Invite Driver
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-1">
            <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 p-3 text-xs text-blue-700 dark:text-blue-300">
              <i className="fas fa-info-circle mr-1"></i>
              The driver will receive an email from <strong>noreply@integratedtech.ca</strong> with a secure link to set up their account and upload the required documents.
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Email Address *</Label>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="driver@example.com"
                />
              </div>
              <div>
                <Label className="text-xs">Full Name (optional)</Label>
                <Input
                  value={inviteForm.full_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs mb-1 block">User Type</Label>
              <select
                value={newDriverUserType}
                onChange={e => setNewDriverUserType(e.target.value)}
                className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
              >
                <option value="driver">Driver</option>
                <option value="owner_operator">Owner Operator</option>
              </select>
            </div>

            <div>
              <Label className="text-xs mb-2 block">
                Required Documents *
                <span className="text-muted-foreground font-normal ml-1">— driver must upload these to complete setup</span>
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {(newDriverUserType === 'owner_operator'
                  ? [...REQUIRED_DOC_OPTIONS, ...INVITE_OO_EXTRA_DOCS]
                  : REQUIRED_DOC_OPTIONS
                ).map(doc => {
                  const checked = inviteForm.required_documents.includes(doc.value);
                  return (
                    <button
                      key={doc.value}
                      type="button"
                      onClick={() => toggleRequiredDoc(doc.value)}
                      className={`flex items-center gap-2 p-3 rounded-lg border text-left text-xs transition-colors ${
                        checked
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300'
                          : 'border-border bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        checked ? 'bg-blue-600 border-blue-600' : 'border-muted-foreground'
                      }`}>
                        {checked && <i className="fas fa-check text-white" style={{ fontSize: '9px' }}></i>}
                      </div>
                      <i className={`fas ${doc.icon} w-4 text-center`}></i>
                      <span className="font-medium">{doc.label}</span>
                    </button>
                  );
                })}
              </div>
              {inviteForm.required_documents.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {inviteForm.required_documents.length} document{inviteForm.required_documents.length > 1 ? 's' : ''} required
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleInviteDriver}
              disabled={inviteLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {inviteLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane mr-2"></i>
                  Send Invite
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverManagement;
